import {Box, Button, Divider, IconButton, MenuItem, Pagination, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import { TEXT_TITLE_COLOR } from '../../constants/colors';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FormBannerManageView from './form';
import TabPanelComponent from './components/tabPanel';
import { IMAGE_POPUP_ROOT_URL } from '../../constants/imageUrl';
import { convertDateStringToNormalString } from '../../utils/convertDateFormat';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import { BannerModel } from '../../models/banner';
import TabPanelActiveComponent from './components/tabPanelActive';
import { BannerEnum } from '../../enums/bannerEnum';
import StatusChip from '../../components/StatusChip';
import { useTranslation } from 'react-i18next';
import LoadingAnimatedIcon from '../../components/loading/loadingAnimated';
import { useBannerController } from './controller/index';

const BannerView = () => {
  const controller = useBannerController();
  const { t } = useTranslation("global");

  return (
    <>
      {controller.openForm ? (
        <FormBannerManageView onClose={controller.handleCloseForm} bannerType={controller.tabBannerType} selectedData={controller.selectedData} onGetData={controller.handleGetData} branchData={controller.merchantData} tagData={controller.tagPromotionData} />
      ) : (
        <>
          {
            !controller.loading
              ?
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color={TEXT_TITLE_COLOR}>
                    {t("banner_page.Banner management")}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'end', mt: 4, mb: 2 }}>
                  <Button onClick={controller.handleOpenForm} variant="contained" sx={{ textTransform: 'none', borderRadius: '10px', background: '#11A2F1' }}>
                    <AddIcon />
                    &nbsp; {t("banner_page.Create Banner")}
                  </Button>
                </Box>
                <Box sx={{ mb: 2, mt: 2, background: '#F4F6F8', borderBottom: 1, borderColor: 'divider' }}>
                  <TabPanelComponent value={controller.tabBannerType} onChange={controller.handleBannerTypeChange} />
                </Box>
                <Box sx={{ mb: 2, mt: 2, background: '#F4F6F8', borderBottom: 1, borderColor: 'divider' }}>
                  <TabPanelActiveComponent value={controller.tabStatus} onChange={controller.handleSetTabStatus} />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <TableContainer>
                    <Table>
                      <TableHead sx={{ background: '#F4F6F8' }}>
                        <TableRow>
                          <TableCell sx={{ borderBottom: 'none' }}>{t("banner_page.No")}.</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>{t("banner_page.Image")}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>{t("banner_page.Start Date")}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>{t("banner_page.End Date")}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>{t("banner_page.Status")}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}>{t("banner_page.Type")}</TableCell>
                          <TableCell sx={{ borderBottom: 'none' }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          controller.rows?.map((item: BannerModel, index: number) => {
                            const getBannerType = (item: BannerModel) => (
                              item?.productId ? BannerEnum.PRODUCT_DETAIL :
                                item?.promotionTagId ? BannerEnum.PROMOTION :
                                  item?.subCategoryId ? BannerEnum.CATEGORY :
                                    BannerEnum.LANDING_PAGE
                            );
                            return <TableRow key={index}>
                              <TableCell sx={{ borderBottom: 'none' }}>{controller.handleCountIndex(index)}</TableCell>
                              <TableCell sx={{ borderBottom: 'none' }}>
                                <img key={item?.id} src={IMAGE_POPUP_ROOT_URL + item?.image?.url} alt="" style={{ width: 50, height: 50 }} />
                              </TableCell>
                              <TableCell sx={{ borderBottom: 'none' }}>{convertDateStringToNormalString(item?.startDate)}</TableCell>
                              <TableCell sx={{ borderBottom: 'none' }}>{convertDateStringToNormalString(item?.endDate)}</TableCell> 
                              <TableCell sx={{ borderBottom: 'none' }}><StatusChip status={item?.isActive} /></TableCell>
                              <TableCell sx={{ borderBottom: 'none' }}>{t(`banner_page.${getBannerType(item)}`)}</TableCell>
                              <TableCell sx={{ borderBottom: 'none' }} align="right">
                                <IconButton size="large" color="inherit" onClick={(e: any) => controller.onOpenMenu(e, item?.id)}>
                                  <MoreHorizIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          })
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Divider sx={{ mt: 1, mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Pagination color="primary" onChange={controller.handlePageChange} page={controller.currentPage} count={controller.countPagination} />
                  </Box>
                </Box>
                <Popover
                  open={Boolean(controller.openMenu)}
                  anchorEl={controller.openMenu}
                  onClose={controller.onCloseMenu}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {p: 1, width: 140, '& .MuiMenuItem-root': {px: 1, typography: 'body2', borderRadius: 0.75}},
                  }}
                >
                  <MenuItem onClick={controller.handleClickEditButton}>
                    <EditIcon />
                    &nbsp;&nbsp;
                    {t('popup_page.Edit')}
                  </MenuItem>

                  <MenuItem sx={{ color: 'error.main' }} onClick={controller.handleClickDeleteButton}>
                    <DeleteIcon />
                    &nbsp;&nbsp;
                    {t('popup_page.Delete')}
                  </MenuItem>
                </Popover>
              </Box>
              :
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingAnimatedIcon />
              </Box>
          }

          <ConfirmDialog
            open={controller.openDeleteDialog}
            title={`${t('validatr_promotion.Do you want to delete data')}`}
            loading={controller.loadingDelete}
            onCancel={() => controller.handleSetOpenDeleteDialog(false)}
            onConfirm={() => controller.handleDeleteData()}
          />

        </>
      )}
    </>
  );
};
export default BannerView;
